import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';

const TITLE_SUFFIX = 'Takove';

// Pathname of the last page that handled route focus. `null` until the first
// page mounts, which is how "first load" is told apart from a navigation: a
// page remounts on every navigation, so per-instance state cannot do it.
let lastPathname: string | null = null;

/** Restores the initial state; wired into the frontend test setup. */
export function resetPageHeading(): void {
  lastPathname = null;
}

export interface PageHeadingProps {
  /** Added to the base `text-3xl`; pass weight or `card-title`, not a size. */
  className?: string;
  /** The page title, already translated. Also the document title (`<title> — Takove`). */
  children: string;
  /**
   * What counts as "a different page" for the focus move; defaults to the
   * pathname. A page whose sub-routes are tabs of one record passes the
   * record's own key, so switching tabs keeps focus on the tab.
   */
  focusKey?: string;
}

/**
 * The page's only `<h1>` (the layout brand is not a heading). Owns the document
 * title and moves focus to itself when the pathname changes, so a screen reader
 * announces the new page without a live region. Not on first load, and not on
 * query or hash changes. A `:id` change is a pathname change, so it moves focus.
 * `focusKey` replaces the pathname as the page identity when only a part of it
 * (a tab) changes.
 */
export function PageHeading({ className, children, focusKey }: PageHeadingProps) {
  const { pathname } = useLocation();
  const key = focusKey ?? pathname;
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = `${children} — ${TITLE_SUFFIX}`;
  }, [children]);

  useEffect(() => {
    if (lastPathname !== null && lastPathname !== key) {
      ref.current?.focus();
    }
    lastPathname = key;
  }, [key]);

  return (
    <h1
      ref={ref}
      tabIndex={-1}
      className={clsx('text-3xl focus:outline-none', className)}
    >
      {children}
    </h1>
  );
}
